import axios, { AxiosResponse } from 'axios';
import { useSelector } from 'react-redux';
import { ArnApplicationFormData, PoiVerificationMyGovApplicationFormData } from 'types/forms/arnApplicationTypes';
import { ICreateSubmissionRequest } from 'types/services/baseServiceTypes';
import { createServiceInstance } from '../baseService';
import {
  IGetArnReferenceDataRequest,
  IGetArnReferenceDataResponse,
  IGetAddressRequest,
  IGetAddressResponse,
  IArnApplicationSubmissionResponse,
  IGetAddressComponentDetailsResponse,
  IDvsCheckResponse,
  IDvsCheckRequest,
  IHasArnOrPendingSubmissionResponse
} from './arnServiceTypes';

let cancel;

export interface IArnService {
  getArnReferenceData: (request?: IGetArnReferenceDataRequest) => Promise<AxiosResponse<IGetArnReferenceDataResponse>>;
  getAddress: (request?: IGetAddressRequest) => Promise<AxiosResponse<IGetAddressResponse>>;
  getAddressComponentDetails: (request?: string) => Promise<AxiosResponse<IGetAddressComponentDetailsResponse>>;
  submitCreateArnApplication: (request: ICreateSubmissionRequest<ArnApplicationFormData>) => Promise<AxiosResponse<IArnApplicationSubmissionResponse>>;
  submitLinkArnApplication: (request: ICreateSubmissionRequest<ArnApplicationFormData>) => Promise<AxiosResponse<IArnApplicationSubmissionResponse>>;
  submitCreateMyCasaArnApplication: (request: FormData) => Promise<AxiosResponse<IArnApplicationSubmissionResponse>>;
  submitLinkMyCasaArnApplication: (request: FormData) => Promise<AxiosResponse<IArnApplicationSubmissionResponse>>;
  getDvsCheckResult: (request?: IDvsCheckRequest) => Promise<AxiosResponse<IDvsCheckResponse>>;
  getHasArnOrPendingSubmissionResponse: () => Promise<AxiosResponse<IHasArnOrPendingSubmissionResponse>>;
  submitPoiVerificationApplicationMyCasa: (request: FormData) => Promise<AxiosResponse<IArnApplicationSubmissionResponse>>;
  submitPoiVerificationApplicationMyGov: (request: ICreateSubmissionRequest<PoiVerificationMyGovApplicationFormData>) => Promise<AxiosResponse<IArnApplicationSubmissionResponse>>;
}

/**
 * ARN service hook
 *
 * @description Gives access to all services relating to ARN
 */
const useArnService = (): IArnService => {
  const token = useSelector(() => useSelector((state: any) => state.B2CSettings.items.accesstokenForms));
  const webappurlFormsApi = useSelector(() => useSelector((state: any) => state.B2CSettings.items.webappurlFormsApi));
  const getInstance = () => createServiceInstance(`${webappurlFormsApi}/api/myCASA/Forms/drone`, token);
  const getArnInstance = () => createServiceInstance(`${webappurlFormsApi}/api/myCASA/Forms/arn`, token);

  const getArnReferenceData = (request?: IGetArnReferenceDataRequest): Promise<AxiosResponse<IGetArnReferenceDataResponse>> => {
    const axiosInstance = getInstance();

    return axiosInstance.post<IGetArnReferenceDataResponse>('/GetReferenceDataTypes/v1', request);
  };

  const getAddressData = (request?: IGetAddressRequest): Promise<AxiosResponse<IGetAddressResponse>> => {
    const axiosInstance = getArnInstance();
    const CancelToken = axios.CancelToken;
    if (cancel != undefined) {
      cancel();
    }
    return axiosInstance.post<IGetAddressResponse>('/GetAddress/v1', request,
      {
        cancelToken: new CancelToken(function executor (c) {
          cancel = c;
        })
      }
    );
  };

  const getAddressComponentDetailsData = (request?: string): Promise<AxiosResponse<IGetAddressComponentDetailsResponse>> => {
    const axiosInstance = getArnInstance();
    return axiosInstance.post<IGetAddressComponentDetailsResponse>('GetAddressComponent/v1', {id: request});
  };

  const submitCreateArnApplicationMyGovPath = (request?: ICreateSubmissionRequest<ArnApplicationFormData>): Promise<AxiosResponse<IArnApplicationSubmissionResponse>> => {
    const axiosInstance = getArnInstance();

    return axiosInstance.post<IArnApplicationSubmissionResponse>('mygovid/create/submission/v1', request);
  };

  const submitLinkArnApplicationMyGovPath = (request?: ICreateSubmissionRequest<ArnApplicationFormData>): Promise<AxiosResponse<IArnApplicationSubmissionResponse>> => {
    const axiosInstance = getArnInstance();

    return axiosInstance.post<IArnApplicationSubmissionResponse>('mygovid/link/submission/v1', request);
  };

  const submitCreatArnApplicationDvsPath = (request?: FormData): Promise<AxiosResponse<IArnApplicationSubmissionResponse>> => {
    const axiosInstance = getArnInstance();

    return axiosInstance.post<IArnApplicationSubmissionResponse>('mycasa/create/submission/v1', request,
      {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Accept': 'application/json',
          'Content-Type': 'multipart/form-data'
        }
      });
  };

  const submitLinkArnApplicationDvsPath = (request?: FormData): Promise<AxiosResponse<IArnApplicationSubmissionResponse>> => {
    const axiosInstance = getArnInstance();

    return axiosInstance.post<IArnApplicationSubmissionResponse>('mycasa/link/submission/v1', request,
      {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Accept': 'application/json',
          'Content-Type': 'multipart/form-data'
        }
      });
  };

  const postIdentityDetailsForDvsCheck = (request?: IDvsCheckRequest): Promise<AxiosResponse<IDvsCheckResponse>> => {
    const axiosInstance = getArnInstance();

    return axiosInstance.post<IDvsCheckResponse>('dvs/validate/v1', request, {
      // Don't throw when the status code is 400
      validateStatus: status => (status >= 200 && status < 300) || status === 400
    });
  };

  const getHasArnOrPendingSubmission = (): Promise<AxiosResponse<IHasArnOrPendingSubmissionResponse>> => {
    const axiosInstance = getArnInstance();

    return axiosInstance.post<IHasArnOrPendingSubmissionResponse>('GetHasArnOrPendingSubmission/v1', {});
  };

  const submitPoiVerificationApplicationDvsPath = (request?: FormData): Promise<AxiosResponse<IArnApplicationSubmissionResponse>> => {
    const axiosInstance = getArnInstance();

    return axiosInstance.post<IArnApplicationSubmissionResponse>('mycasa/poiverification/submission/v1', request,
      {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Accept': 'application/json',
          'Content-Type': 'multipart/form-data'
        }
      });
  };

  const submitPoiVerificationApplicationMyGovPath = (request?: ICreateSubmissionRequest<PoiVerificationMyGovApplicationFormData>): Promise<AxiosResponse<IArnApplicationSubmissionResponse>> => {
    const axiosInstance = getArnInstance();

    return axiosInstance.post<IArnApplicationSubmissionResponse>('mygovid/poiverification/submission/v1', request);
  };

  return {
    getArnReferenceData: getArnReferenceData,
    getAddress: getAddressData,
    getAddressComponentDetails: getAddressComponentDetailsData,
    submitCreateArnApplication: submitCreateArnApplicationMyGovPath,
    submitLinkArnApplication: submitLinkArnApplicationMyGovPath,
    submitCreateMyCasaArnApplication: submitCreatArnApplicationDvsPath,
    submitLinkMyCasaArnApplication: submitLinkArnApplicationDvsPath,
    getDvsCheckResult: postIdentityDetailsForDvsCheck,
    getHasArnOrPendingSubmissionResponse: getHasArnOrPendingSubmission,
    submitPoiVerificationApplicationMyCasa: submitPoiVerificationApplicationDvsPath,
    submitPoiVerificationApplicationMyGov: submitPoiVerificationApplicationMyGovPath
  };
};

export default useArnService;